import { Form, Input } from 'antd';
import { RevxDiscTextItem } from 'components/RevxDiscTextItem';
import { JobOptionSvc, JobOptionSvcGroup, ServiceOptionTypeEnum } from 'domains/jobconfigDomain';
import { useRole } from 'hooks';
import { uniqBy } from 'lodash-es';
import { FieldError } from 'rc-field-form/lib/interface';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ConfigSvcDetail, { ConfigSvcProps } from '.';
import CommonJobSvcBorder from './common/CommonJobSvcBorder';
import CommonOneofFormCheckbox from './common/CommonOneofFormCheckbox';
import CommonOneofFormRadio from './common/CommonOneofFormRadio';
import CommonOneofFormTabs from './common/CommonOneofFormTabs';
import CommonOneofSvcInstructions from './common/CommonOneofSvcInstructions';
import CommonOneofSvcMessage from './common/CommonOneofSvcMessage';
import CommonOneofSvcName from './common/CommonOneofSvcName';
import CommonOneofSvcOption from './common/CommonOneofSvcOption';
import CommonOneofSvcQuantityPerBundle from './common/CommonOneofSvcQuantityPerBundle';
import CommonOneofSvcSpec from './common/CommonOneofSvcSpec';
import { useOneofId } from './hook/useOneofId';
import { useSvcBundling } from './hook/useSvcBundling';
import { BundlingRenderTypeEnum } from './hook/useSvcBundlingList';
import { useSvcHasImg } from './hook/useSvcHasImg';
import RevxSvcContent from './RevxSvcContent';
export interface IOneofProps extends Partial<ConfigSvcProps> {
  svcGroup?: JobOptionSvcGroup;
}

const OneOfSvcs = (props: IOneofProps) => {
  const { formCache, form, svcGroup, disabled } = props;
  const id = useOneofId(props);
  const { buyerRole } = useRole();

  const visibleSvcs = useMemo(() => {
    return (
      svcGroup?.svcs?.filter((svc) => {
        return !(buyerRole && svc.name === 'File Prep /per hr.');
      }) || []
    );
  }, [buyerRole, svcGroup?.svcs]);

  const svcHasImg = useSvcHasImg({ svcs: visibleSvcs });

  const svcHasBundling = useSvcBundling({ svcGroup });

  const [formErrorTip, setFormErrorTip] = useState<FieldError[]>();

  const checkForm = useCallback(async () => {
    try {
      await form?.validateFields();
      setFormErrorTip([]);
    } catch {
      setFormErrorTip(form?.getFieldsError());
    }
  }, [form]);

  const hideSvcOption = (svc: JobOptionSvc | undefined) => {
    if (
      Boolean(
        !svc?.options &&
          (!svc?.specs || svc.specs?.length <= 1) &&
          !svc?.showInstructions &&
          svc?.typeName !== 'Bundling' &&
          svc?.uomCoefficient !== 'BUNDLE_COUNT'
      )
    ) {
      return true;
    } else {
      return false;
    }
  };

  const svc = useMemo(() => {
    return visibleSvcs.find((svc) => svc.id === formCache?.[id]?.['svc']);
  }, [formCache, id, visibleSvcs]);

  useEffect(() => {
    if (!visibleSvcs.length || !formCache?.[id]?.svc) {
      return;
    }

    const matchedSvc = visibleSvcs.find((item) => item.id === formCache[id].svc);
    if (!matchedSvc) {
      form?.setFieldsValue({
        [id]: {
          ...formCache[id],
          svc: visibleSvcs[0].id,
        },
      });
      formCache[id].svc = visibleSvcs[0].id;
    }
  }, [form, formCache, id, visibleSvcs]);

  useEffect(() => {
    if (formCache && !formCache[id]) {
      formCache[id] = {};
    }
    svc?.parameterLabels?.forEach((parameterLabel) => {
      const specLabel = uniqBy(
        svc?.specs.map((spec) => {
          return {
            value:
              spec.parameters.find((param) => param.label === parameterLabel)
                ?.parameter || '',
            children: (
              <span>
                {
                  spec.parameters.find(
                    (param) => param.label === parameterLabel
                  )?.parameter
                }
              </span>
            ),
          };
        }),
        'value'
      ).map((item) => item.value);

      if (
        formCache &&
        (!formCache?.[id]?.spec?.[parameterLabel] ||
          !specLabel?.includes(formCache?.[id]?.spec?.[parameterLabel]))
      ) {
        if (!formCache[id].spec) {
          formCache[id].spec = {};
        }
        formCache[id].spec[parameterLabel] = svc?.specs[0].parameters.find(
          (item) => item.label === parameterLabel
        )?.parameter;
      }
    });
    svc?.options?.forEach((option) => {
      if (option.type === ServiceOptionTypeEnum.INPUT) {
        if (formCache && formCache[id][option.label] === undefined && option.defaultValue !== undefined) {
          formCache[id][option.label] = option.defaultValue;
        }
      } else if (
        formCache &&
        !option.values?.includes(formCache?.[id]?.[option.label]) &&
        option?.values?.[0]
      ) {
        formCache[id][option.label] = option?.values?.[0];
      }
    });
    // if (
    //   svc?.uomCoefficient === UOMCoefficientEnum.BUNDLE_COUNT &&
    //   formCache &&
    //   !formCache?.[id].quantityPerBundle
    // ) {
    //   formCache[id].quantityPerBundle = 1;
    // }
    form?.setFieldsValue(formCache);
    checkForm();
  }, [formCache, svc, form, id, checkForm]);

  const svcDisabled = useMemo(() => {
    return !svcGroup?.isRequired && !formCache?.[id]?.name;
  }, [formCache, id, svcGroup?.isRequired]);

  const isBundling = useMemo(() => {
    return svcHasImg && svcHasBundling && !svcGroup?.isRequired;
  }, [svcHasImg, svcHasBundling, svcGroup?.isRequired]);

  if (!visibleSvcs.length) {
    return null;
  }

  return (
    <>
      {isBundling ? (
        <>
          {Boolean(
            svcHasBundling?.renderType === BundlingRenderTypeEnum.DISC
          ) && (
            <RevxDiscTextItem style={{ marginTop: '28px' }}>
              <div>
                <p style={{ marginBottom: 0 }}>{svcHasBundling?.text}</p>
              </div>
            </RevxDiscTextItem>
          )}
          {Boolean(
            svcHasBundling?.renderType === BundlingRenderTypeEnum.DEFAULT
          ) && (
            <div
              className="jobconfig-jobOption-content-subTitle"
              style={{ marginTop: 24 }}
            >
              {svcHasBundling?.text}
            </div>
          )}
          <Form.Item name={[id, 'name']} noStyle>
            <Input hidden />
          </Form.Item>
        </>
      ) : (
        <CommonOneofSvcName {...props} />
      )}

      <div className="jobconfig-jobOption-tighten">
        <div style={{ marginRight: 30 }}>
          {/* {Boolean(
            svcGroup?.svcs[0]?.uomCoefficient === UOMCoefficientEnum.BUNDLE_COUNT
          ) && (
              <div
                className="jobconfig-jobOption-content-label"
                style={{ marginBottom: 14, opacity: 0 }}
              >
                type
              </div>
            )} */}

          {/* <CommonFormTabs list={svcGroup?.svcs} disabled={svcDisabled} /> */}
          {/* <CommonOneofFormTabs list={svcGroup?.svcs} disabled={svcDisabled} /> */}
          <Form.Item name={[id, 'svc']} noStyle>
            {svcHasImg ? (
              isBundling ? (
                <CommonOneofFormCheckbox
                  {...props}
                  list={visibleSvcs}
                  disabled={(svcDisabled && !isBundling) || disabled}
                />
              ) : (
                <CommonOneofFormRadio
                  {...props}
                  list={visibleSvcs}
                  disabled={(svcDisabled && !isBundling) || disabled}
                />
              )
            ) : (
              <CommonOneofFormTabs
                {...props}
                list={visibleSvcs}
                disabled={svcDisabled || disabled}
              />
            )}
          </Form.Item>
          {/* <Select
            className="jobconfig-jobOption-select"
            disabled={svcDisabled}
            getPopupContainer={(e) => {
              return e as HTMLElement;
            }}
          >
            {svcGroup?.svcs?.map((svc) => (
              <Option value={svc.id}>{svc.name}</Option>
            ))}
          </Select> */}
        </div>
        <div
          className="base-service-container"
          hidden={
            (!svcGroup?.isRequired && !formCache?.[id]?.name) ||
            !svc ||
            hideSvcOption(svc)
          }
        >
          {Boolean(svc) && (
            <>
              {svc?.revxTypeId === 1 ||
              (svc?.revxId === 15 && svc?.revxTypeId === 18) ? (
                <div className="jobconfig-jobOption-hide-border">
                  {Boolean(svc) && (
                    <ConfigSvcDetail
                      {...svc}
                      {...props}
                      revxTypeId={svc?.revxTypeId || 0}
                      revxId={svc?.revxId || 0}
                      id={+id}
                      isRequired={true}
                      jobOptionSvc={svc}
                      isOneof={true}
                    />
                  )}
                </div>
              ) : (
                <>
                  <CommonOneofSvcQuantityPerBundle
                    {...props}
                    formErrorTip={formErrorTip}
                    svc={svc}
                  />
                  <CommonOneofSvcSpec {...props} svc={svc} />
                  <CommonOneofSvcOption {...props} svc={svc} />
                  <CommonOneofSvcInstructions {...props} svc={svc} />
                  <CommonOneofSvcMessage message={svc?.message || ''} />
                </>
              )}
            </>
          )}
        </div>
      </div>
      <CommonJobSvcBorder />
      {/* {Boolean(svc) ? (
        <CommonJobSvcBorder />
      ) : (
        <div className="jobconfig-jobOption-content-border"></div>
      )} */}
      {Boolean(svcGroup?.svcGroups.length) && (
        <RevxSvcContent svcGroups={svcGroup?.svcGroups} {...props} />
      )}
    </>
  );
};

export default OneOfSvcs;
